import clsx from 'clsx';
import React from 'react';
import {Box} from '@mui/material';
import {Body, Tooltip} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import type {CampaignCreatorRow} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import Tag from '@/components/tag/Tag';
import styles from './AssetsCell.module.scss';
import AssetsHoverDetail from './AssetsHoverDetail';
import AssetsProgressMeter from './AssetsProgressMeter';
import {deriveAssetsStatus, isEffectivelyOverdue} from './assetsCellStatus';

interface AssetsCellProps {
  creator: CampaignCreatorRow;
  campaignNetwork?: string | null;
}

function AssetsCell({creator, campaignNetwork}: AssetsCellProps) {
  const {
    acceptedAssetsCount,
    requiredAssetsCount,
    pendingAssetsCount,
    editsRequestedAssetsCount,
    notSubmittedCount,
    extraAssetsCount,
    isOverdue,
    dueSoon,
  } = creator;

  if (acceptedAssetsCount == null || requiredAssetsCount == null) {
    return <Body size="md">-</Body>;
  }

  const status = deriveAssetsStatus(creator);
  const effectivelyOverdue = isEffectivelyOverdue(creator);

  // Counts are capped per kind by the serializer, so requiredAssetsCount is the
  // meaningful denominator. A gig with no requirement has nothing to chart —
  // surface the status on its own rather than a "0/0" meter.
  if (requiredAssetsCount === 0) {
    return status ? (
      <Box component="span" className={styles.cell}>
        <Tag label={status.label} color={status.color} size="small" />
      </Box>
    ) : (
      <Body size="md">-</Body>
    );
  }

  const extraCount = extraAssetsCount ?? 0;

  return (
    <Tooltip
      title={
        <AssetsHoverDetail
          creator={creator}
          campaignNetwork={campaignNetwork}
        />
      }
      arrow
      placement="top"
      className={styles.tooltip}
    >
      <Box component="span" className={styles.cell}>
        {status ? (
          <Tag label={status.label} color={status.color} size="small" />
        ) : null}
        <div className={styles.meterRow}>
          <AssetsProgressMeter
            acceptedCount={acceptedAssetsCount}
            pendingCount={pendingAssetsCount ?? 0}
            editsRequestedCount={editsRequestedAssetsCount ?? 0}
            notSubmittedCount={notSubmittedCount ?? 0}
          />
          <Body
            size="sm"
            className={clsx(styles.count, {
              // Red count matches the Overdue pill (past due, no uploads yet).
              // dueSoon is also true when past due, so keep gating yellow on
              // raw isOverdue — otherwise submitted past-due rows look "due soon".
              [styles.countOverdue]: effectivelyOverdue,
              [styles.countDueSoon]: !isOverdue && dueSoon,
            })}
          >
            {`${acceptedAssetsCount}/${requiredAssetsCount}`}
          </Body>
          {extraCount > 0 ? (
            <Body size="sm" className={styles.extra}>
              {translate('+{count}', {count: extraCount})}
            </Body>
          ) : null}
        </div>
      </Box>
    </Tooltip>
  );
}

export default React.memo(AssetsCell);
